// Production API contracts (M5, WP5.1): recipes (drafts, the baker's sign-off, retiring),
// the standard cost roll-up with its coverage, production batches (plan, cancel, read) and
// the production reports. Wire format as elsewhere: snake_case, quantities as decimal
// strings, money as integer cents, instants ISO-8601 UTC, days `YYYY-MM-DD`.
//
//   GET  /recipes                                     recipes, searchable
//   POST /recipes                                     a recipe for a product, with draft v1
//   GET  /recipes/:id                                 versions, generated statement, costs
//   POST /recipes/:id/versions                        a new draft (empty body = copy latest)
//   PUT  /recipes/:id/versions/:versionId             edit a draft
//   POST /recipes/:id/versions/:versionId/sign-off    the baker signs off: it becomes active
//   POST /recipes/:id/versions/:versionId/retire      retire a draft or the active version
//   GET  /costs/products/:id                          one product's roll-up, line by line
//   POST /costs/rollup                                recompute (and store) standard costs
//   GET  /production/batches                          batches by day / branch / status
//   POST /production/batches                          plan a batch in the Admin (PB-SRV-…)
//   GET  /production/batches/:id                      consumption, output lot, variance, cost
//   POST /production/batches/:id/cancel               cancel a planned or started batch
//   GET  /production/reports/yield-variance           per product over a period
//   GET  /production/reports/usage                    actual vs theoretical component usage
//
// Permissions: reads `reports.view` (batches within the caller's branch scope); recipe
// drafts `recipe.edit`; sign-off `recipe.signoff`; retire `recipe.edit`; cost roll-up
// `catalogue.edit` (all branches); planning and cancelling batches `production.plan`.
import { z } from 'zod';
import { lossPctSchema, recipeVersionStatusSchema } from '../catalogue/recipes.js';
import {
  productRoleSchema,
  productRolesSchema,
  deductionModeSchema,
  serviceKindSchema,
} from '../catalogue/roles.js';
import { stateKeySchema } from '../catalogue/state-rules.js';
import { flagSchema } from '../commands/envelope.js';
import { batchAllocationInputSchema } from '../commands/production.js';
import { allergenSchema } from '../production/allergens.js';
import { BATCH_DESTINATIONS, batchStatusSchema } from '../production/batches.js';
import { COST_SOURCES } from '../production/costing.js';
import { positiveQtyString, qtyString } from '../quantity/index.js';
import { isPlainDate } from '../time/index.js';
import { uomCode } from '../uom/index.js';
import { catalogueRecipeLineSchema } from './catalogue.js';
import {
  STOCK_PAGE_DEFAULT,
  STOCK_PAGE_MAX,
  stockLocationRefSchema,
  stockProductRefSchema,
} from './stock.js';
import { branchRefSchema, lotRefSchema, personRefSchema } from './stock-control.js';

const instant = z.iso.datetime({ offset: true });
const plainDate = z.string().refine(isPlainDate, 'Expected a date, YYYY-MM-DD');
const count = z.int().min(0);
const limit = z.coerce.number().int().min(1).max(STOCK_PAGE_MAX).default(STOCK_PAGE_DEFAULT);
const cursor = z.string().min(1).max(512).optional();

/** `:id` path parameter. */
export const productionIdParamsSchema = z.object({ id: z.uuid() });
/** `:id/versions/:versionId` path parameters. */
export const recipeVersionParamsSchema = z.object({ id: z.uuid(), versionId: z.uuid() });

// ---------------------------------------------------------------------------------------
// Costs

/** A gap in a cost, with its code and the words the Admin shows. */
export const costGapSchema = z.object({
  code: z.enum([
    'no_cost',
    'recipe_blocked',
    'component_cost_missing',
    'cycle',
    'missing_conversion',
  ]),
  message: z.string(),
});

/** Coverage of a roll-up: "computed for X of Y; missing: …" (lesson 12). */
export const costCoverageSchema = z.object({
  computed: count,
  total: count,
  missing: z.array(z.object({ product: stockProductRefSchema, gap: costGapSchema })),
  /** The coverage in one sentence. */
  summary: z.string(),
});
/** Coverage of a cost roll-up. */
export type CostCoverageResponse = z.infer<typeof costCoverageSchema>;

/** One line of a product's roll-up. */
export const costLineSchema = z.object({
  component: stockProductRefSchema,
  qty: qtyString,
  uom: uomCode,
  loss_pct: qtyString,
  /** The component's standard cost per base unit, whole cents (`null`: none). */
  unit_cost_cents: z.int().nullable(),
  /** This line's share of one batch, whole cents (`null`: not computable). */
  line_cost_cents: z.int().nullable(),
  gap: costGapSchema.nullable(),
});

/** `GET /costs/products/:id`: how a product's standard cost is made up. */
export const productCostSchema = z.object({
  product: stockProductRefSchema,
  /** What is stored on the product now (what approvals and reports use). */
  stored: z.object({
    standard_cost_cents: z.int().nullable(),
    cost_source: z.enum(COST_SOURCES).nullable(),
    cost_updated_at: instant.nullable(),
  }),
  /** The roll-up now: `cents` per base unit, or the gap. */
  computed: z.object({
    cents: z.int().nullable(),
    source: z.enum(COST_SOURCES).nullable(),
    gap: costGapSchema.nullable(),
  }),
  /** The recipe version rolled up (active, else the latest draft); `null`: no recipe. */
  recipe_version: z
    .object({ id: z.uuid(), version_no: z.int().min(1), status: recipeVersionStatusSchema })
    .nullable(),
  batch_yield_qty: qtyString.nullable(),
  yield_uom: uomCode.nullable(),
  lines: z.array(costLineSchema),
  /** Coverage over the product and everything nested under it. */
  coverage: costCoverageSchema,
});
/** A product's cost roll-up. */
export type ProductCost = z.infer<typeof productCostSchema>;

/** `POST /costs/rollup` body: which products (default: all live ones). */
export const costRollupRequestSchema = z.object({
  product_ids: z.array(z.uuid()).min(1).max(5_000).optional(),
});

/** `POST /costs/rollup` response. */
export const costRollupResponseSchema = z.object({
  coverage: costCoverageSchema,
  /** Products whose stored cost changed (`cost_source = rollup`). */
  updated: count,
  /**
   * Which products' stored costs changed, with the cost per base unit before and after
   * (whole cents; `before` `null`: it had none). At most 200, largest change first
   * (UI5A-11: the result says what changed, not only how many).
   */
  changes: z
    .array(
      z.object({
        product: stockProductRefSchema,
        before_cents: z.int().nullable(),
        after_cents: z.int(),
      }),
    )
    .max(200),
  /** Products whose cost a person set (`manual` / `admin`): never overwritten. */
  kept_person_cost: count,
  unchanged: count,
});

// ---------------------------------------------------------------------------------------
// Recipes

/** `GET /recipes` query. */
export const recipeListQuerySchema = z.object({
  /** Product name contains (case ignored). */
  q: z.string().trim().min(1).max(100).optional(),
  /**
   * `active`: has a signed-off version; `draft`: has a draft waiting for sign-off (a change to
   * a recipe in use included, UI5A-3); `never_signed`: only drafts, never signed off;
   * `none`: only retired versions.
   */
  state: z.enum(['active', 'draft', 'never_signed', 'none']).optional(),
  limit,
  cursor,
});

/** A recipe in the list. */
export const recipeSummarySchema = z.object({
  id: z.uuid(),
  product: stockProductRefSchema.extend({
    roles: z.array(productRoleSchema),
    /** The product's declared allergens (what the signed-off statement says). */
    allergens: z.array(allergenSchema),
  }),
  active_version: z
    .object({ id: z.uuid(), version_no: z.int().min(1), signed_off_at: instant })
    .nullable(),
  draft_count: count,
  latest_version_no: z.int().min(1),
  /**
   * The version the cost rolls up from (active, else the latest draft; D-058) and its batch
   * yield, so a list can show the cost of one batch. `null`: no live version.
   */
  costed_version: z
    .object({
      id: z.uuid(),
      version_no: z.int().min(1),
      status: recipeVersionStatusSchema,
      batch_yield_qty: qtyString,
      yield_uom: uomCode,
    })
    .nullable(),
  /**
   * Things to look at: precision warnings on the newest draft waiting for sign-off (never
   * blocking; a signed version's were seen at its sign-off, UI5A-14), plus one when the
   * signed statement is out of date (`statement_out_of_date`).
   */
  warning_count: count,
  /**
   * The allergens the costed version's ingredients contain (the generated statement's,
   * nested recipes included). The product's declared `allergens` should include every one;
   * a list that doesn't is shown as a warning, never as "None declared" (UI5A-1).
   */
  ingredient_allergens: z.array(allergenSchema),
  /**
   * One batch of the costed version at the costs now, line by line — the same sum as the
   * cost page's (never a rounded unit cost × yield, UI5A-8). `null`: a line has no cost.
   */
  batch_cost_cents: z.int().nullable(),
  /** Why the costed version can't be costed now (`null`: it can, or there is no recipe). */
  cost_gap: costGapSchema.nullable(),
  /**
   * The active version's signed statement no longer names every allergen its ingredients
   * contain (a component gained one since sign-off; D-065, CR5-6): sign off a new version.
   */
  statement_out_of_date: z.boolean(),
  standard_cost_cents: z.int().nullable(),
  cost_source: z.enum(COST_SOURCES).nullable(),
  updated_at: instant,
});
/** A recipe in the list. */
export type RecipeSummary = z.infer<typeof recipeSummarySchema>;

/** `GET /recipes` response. */
export const recipeListResponseSchema = z.object({
  items: z.array(recipeSummarySchema),
  next_cursor: z.string().nullable(),
  total: count,
});

/** One line of a draft, as the Admin sends it. */
export const recipeLineInputSchema = z.object({
  component_product_id: z.uuid(),
  /** Full precision, per batch. */
  qty: positiveQtyString,
  uom: uomCode,
  loss_pct: lossPctSchema.default('0'),
  allowed_states: z
    .array(stateKeySchema)
    .refine((k) => new Set(k).size === k.length, 'States must not repeat')
    .default([]),
});

/** A draft's content (a full replace on edit). */
export const recipeDraftInputSchema = z.object({
  batch_yield_qty: positiveQtyString,
  yield_uom: uomCode,
  instructions: z.string().trim().max(20_000).nullable().default(null),
  /** `null`: not written yet (the generated statement is offered at sign-off). */
  allergen_statement: z.string().trim().max(5_000).nullable().default(null),
  lines: z.array(recipeLineInputSchema).min(1).max(200),
});
/** A draft's content. */
export type RecipeDraftInput = z.infer<typeof recipeDraftInputSchema>;

/** `POST /recipes` body: a product's first recipe, as draft version 1. */
export const recipeCreateRequestSchema = z.object({
  product_id: z.uuid(),
  draft: recipeDraftInputSchema,
});

/** `POST /recipes/:id/versions` body: new draft content, or a copy of a version (default: the latest). */
export const recipeVersionCreateRequestSchema = z.object({
  from_version_id: z.uuid().optional(),
  draft: recipeDraftInputSchema.optional(),
});

/** `POST …/sign-off` body: the statement exactly as the baker signs it. */
export const recipeSignOffRequestSchema = z.object({
  allergen_statement: z.string().trim().min(1).max(5_000),
  /**
   * Lines with a precision warning the baker confirmed as exact ("It's exact", UI5A-14):
   * recorded with the sign-off in the audit trail.
   */
  exact_line_ids: z.array(z.uuid()).max(200).optional(),
});

/** `POST …/retire` body. */
export const recipeRetireRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500).optional(),
});

/** The statement generated from a version's lines (a starting point for the baker). */
export const generatedStatementSchema = z.object({
  text: z.string(),
  ingredients: z.array(z.string()),
  allergens: z.array(allergenSchema),
  ordered_by_mass: z.boolean(),
});

/** A version in the recipe detail. */
export const recipeVersionDetailSchema = z.object({
  id: z.uuid(),
  version_no: z.int().min(1),
  status: recipeVersionStatusSchema,
  /** `import`: made by a catalogue import; `person`: written in the Admin; `system`: seeded. */
  origin: z.enum(['import', 'person', 'system']),
  batch_yield_qty: qtyString,
  yield_uom: uomCode,
  instructions: z.string().nullable(),
  allergen_statement: z.string().nullable(),
  generated_statement: generatedStatementSchema,
  signed_off_by: personRefSchema.nullable(),
  signed_off_at: instant.nullable(),
  effective_from: instant.nullable(),
  last_edited_by: personRefSchema.nullable(),
  last_edited_at: instant.nullable(),
  retired_at: instant.nullable(),
  created_at: instant,
  /** Precision warnings on any line (never blocking). */
  warning_count: count,
  /**
   * A signed version's statement against its ingredients now (D-065, CR5-6): the allergens
   * the ingredients contain that the signed statement doesn't name. `null` for a version
   * that was never signed off.
   */
  statement_check: z
    .object({ out_of_date: z.boolean(), missing_allergens: z.array(allergenSchema) })
    .nullable(),
  /**
   * A draft's standard cost if it were signed off now (UI5A-5): per base unit and for one
   * batch, whole cents, or the gap. `null` for a version that isn't a draft. A person's cost
   * on the product is kept at sign-off regardless (the product's `cost.stored` says so).
   */
  cost_if_signed: z
    .object({
      unit_cents: z.int().nullable(),
      batch_cents: z.int().nullable(),
      gap: costGapSchema.nullable(),
    })
    .nullable(),
  /** Planned or started batches pinned to this version (they keep it if it retires). */
  open_batch_count: count,
  /**
   * Warned lines the baker confirmed as exact when signing this version off ("It's exact",
   * UI5A-14; from the sign-off's audit record). Empty for a version never signed off.
   */
  exact_line_ids: z.array(z.uuid()).default([]),
  lines: z.array(catalogueRecipeLineSchema),
});
/** A recipe version in the detail. */
export type RecipeVersionDetail = z.infer<typeof recipeVersionDetailSchema>;

/** `GET /recipes/:id` (and the answer to every recipe write). */
export const recipeDetailSchema = z.object({
  id: z.uuid(),
  product: stockProductRefSchema.extend({
    roles: z.array(productRoleSchema),
    deduction_mode: deductionModeSchema,
    shelf_life_days: z.int().min(0).nullable(),
    allergens: z.array(allergenSchema),
    allergen_statement: z.string().nullable(),
  }),
  archived_at: instant.nullable(),
  /** Newest first. */
  versions: z.array(recipeVersionDetailSchema),
  cost: productCostSchema,
});
/** A recipe with its versions. */
export type RecipeDetail = z.infer<typeof recipeDetailSchema>;

// ---------------------------------------------------------------------------------------
// Catalogue edits and field ownership (D-032, B-026)

/** `PATCH /catalogue/products/:id` body: what a person may edit in V1. */
export const catalogueProductUpdateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    shelf_life_days: z.int().min(0).max(3650).nullable(),
    deduction_mode: deductionModeSchema,
    tracks_expiry: z.boolean(),
    allergens: z.array(allergenSchema).refine((a) => new Set(a).size === a.length),
    /** Per base unit; `null` hands the cost back to the roll-up / the import. */
    standard_cost_cents: z.int().min(0).nullable(),
    /** What a `service` product is (B-031); only a service product takes one. */
    service_kind: serviceKindSchema.nullable(),
    /** What the product is (B-059); at least one, none repeated. */
    roles: productRolesSchema,
    /** Its category, or `null` for none (B-059). */
    category_id: z.uuid().nullable(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'Change at least one field');
/** `PATCH /catalogue/products/:id` body. */
export type CatalogueProductUpdateRequest = z.infer<typeof catalogueProductUpdateRequestSchema>;

/** Fields a person can own on a product (their last writer is recorded). */
export const PERSON_OWNED_PRODUCT_FIELDS = [
  'name',
  'shelf_life_days',
  'deduction_mode',
  'tracks_expiry',
  'allergens',
  'allergen_statement',
  'standard_cost_cents',
  'service_kind',
  'roles',
  'category_id',
] as const;

/**
 * A field an import wanted to change but a person wrote last (D-032): the import left it
 * alone and asks. `keep` the person's value, or `take` the import's.
 */
export const catalogueReviewItemSchema = z.object({
  id: z.uuid(),
  entity: z.object({
    table: z.enum(['product', 'recipe', 'supplier']),
    id: z.uuid(),
    name: z.string(),
    /** The product's base unit (a recipe's: its product's), so a cost reads "per kg". */
    base_uom: uomCode.nullable().optional(),
  }),
  field: z.string(),
  current_value: z.unknown(),
  import_value: z.unknown(),
  /**
   * A recipe item (`field: content`): the products its two versions' lines name, so the
   * review can say which ingredients were added, removed or changed (UI5A-105).
   */
  components: z.array(z.object({ id: z.uuid(), name: z.string(), base_uom: uomCode })).optional(),
  /** Who wrote the current value last. */
  owner: personRefSchema.nullable(),
  owned_at: instant.nullable(),
  import_run_id: z.uuid().nullable(),
  status: z.enum(['open', 'resolved']),
  resolution: z.enum(['kept', 'taken']).nullable(),
  resolved_by: personRefSchema.nullable(),
  resolved_at: instant.nullable(),
  created_at: instant,
  updated_at: instant,
});
/** A field-ownership review item. */
export type CatalogueReviewItem = z.infer<typeof catalogueReviewItemSchema>;

/** `GET /catalogue/review-items` query. */
export const catalogueReviewItemListQuerySchema = z.object({
  status: z.enum(['open', 'resolved']).default('open'),
  limit,
  cursor,
});
/** `GET /catalogue/review-items` response. */
export const catalogueReviewItemListResponseSchema = z.object({
  items: z.array(catalogueReviewItemSchema),
  next_cursor: z.string().nullable(),
  total: count,
});
/** `POST /catalogue/review-items/:id/resolve` body. */
export const catalogueReviewResolveRequestSchema = z.object({
  resolution: z.enum(['keep', 'take']),
});

// ---------------------------------------------------------------------------------------
// Import review: component targets (B-024)

/** Roles a product needs to be offered as a recipe component in the import review. */
export const COMPONENT_TARGET_ROLES = [
  'ingredient',
  'intermediate',
  'packaging',
  'supply',
] as const;

/** `GET /imports/runs/:id/component-targets` query. */
export const importComponentTargetsQuerySchema = z.object({
  q: z.string().trim().min(1).max(100).optional(),
  limit,
});
/**
 * A source record a recipe component can be resolved to (`resolve_component`): it became a
 * product that recipes can use (an ingredient, intermediate, packaging or supply). Records
 * that became an expiry *state* of another product, were ignored or couldn't be classified
 * are never offered.
 */
export const importComponentTargetSchema = z.object({
  source_ref: z.string(),
  record_kind: z.enum(['item', 'variant']),
  item_ref: z.string(),
  name: z.string().nullable(),
  sku: z.string().nullable(),
  roles: z.array(productRoleSchema),
});
/** A record a recipe component can be resolved to. */
export type ImportComponentTarget = z.infer<typeof importComponentTargetSchema>;

/** `GET /imports/runs/:id/component-targets` response. */
export const importComponentTargetsResponseSchema = z.object({
  items: z.array(importComponentTargetSchema),
});

// ---------------------------------------------------------------------------------------
// Batches

/** A yield variance: actual − base, and percent of the base (2 dp; `null` when base is 0). */
export const varianceSchema = z.object({ qty: qtyString, pct: z.string().nullable() });

/** `GET /production/batches` query. `day` is the planned local day; `from`/`to` a range. */
export const batchListQuerySchema = z.object({
  day: plainDate.optional(),
  from: plainDate.optional(),
  to: plainDate.optional(),
  branch_id: z.uuid().optional(),
  status: z
    .string()
    .transform((s) => s.split(',').map((x) => x.trim()))
    .pipe(z.array(batchStatusSchema).min(1))
    .optional(),
  product_id: z.uuid().optional(),
  limit,
  cursor,
});

/** A recipe version reference. */
export const recipeVersionRefSchema = z.object({
  id: z.uuid(),
  recipe_id: z.uuid(),
  version_no: z.int().min(1),
  status: recipeVersionStatusSchema,
});

/** A batch in a list. */
export const batchSummarySchema = z.object({
  id: z.uuid(),
  number: z.string(),
  branch: branchRefSchema,
  product: stockProductRefSchema,
  recipe_version: recipeVersionRefSchema,
  status: batchStatusSchema,
  planned_start: instant,
  /** Trading day of `planned_start` (the account’s zone and cut-off, like a movement’s). */
  planned_day: plainDate,
  planned_qty: qtyString,
  station: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  origin: z.enum(['admin', 'station']),
  started_at: instant.nullable(),
  completed_at: instant.nullable(),
  actual_yield_qty: qtyString.nullable(),
  output_lot: lotRefSchema.nullable(),
  yield_variance: z.object({ vs_planned: varianceSchema, vs_recipe: varianceSchema }).nullable(),
  /** Flags raised at completion (component shortages, …). */
  flag_count: count,
  /** Their codes, each once, in the order first raised (the list names them, UI5A-9). */
  flag_codes: z.array(z.string()),
});
/** A batch in a list. */
export type BatchSummary = z.infer<typeof batchSummarySchema>;

/** `GET /production/batches` response. */
export const batchListResponseSchema = z.object({
  items: z.array(batchSummarySchema),
  next_cursor: z.string().nullable(),
  total: count,
});

/** A batch allocation: planned (from the Admin) or actual (at completion). */
export const batchAllocationSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['planned', 'actual']),
  destination: z.enum(BATCH_DESTINATIONS),
  qty: qtyString,
  location: stockLocationRefSchema.nullable(),
  order_line_id: z.uuid().nullable(),
  /**
   * The order line it is promised to, by name (UI8C-3: a short claim says which order, line
   * and customer). `null` for any other destination.
   */
  order_line: z
    .object({
      id: z.uuid(),
      order_id: z.uuid(),
      order_number: z.string(),
      position: z.int().min(1),
      customer_name: z.string().nullable(),
    })
    .nullable()
    .default(null),
  to_branch: branchRefSchema.nullable(),
});

/** `GET /production/batches/:id` response. */
export const batchDetailSchema = batchSummarySchema.extend({
  input_qty: qtyString.nullable(),
  note: z.string().nullable(),
  created_by: personRefSchema.nullable(),
  created_at: instant,
  started_by: personRefSchema.nullable(),
  completed_by: personRefSchema.nullable(),
  cancelled_at: instant.nullable(),
  cancelled_by: personRefSchema.nullable(),
  cancel_reason: z.string().nullable(),
  allocations: z.array(batchAllocationSchema),
  transitions: z.array(
    z.object({
      transition: z.enum(['start', 'complete', 'cancel']),
      occurred_at: instant,
      by: personRefSchema.nullable(),
      command_id: z.uuid().nullable(),
    }),
  ),
  /** Per component: the recipe's need vs what was taken. */
  components: z.array(
    z.object({
      component: stockProductRefSchema,
      theoretical: qtyString,
      consumed: qtyString,
      beyond_stock: qtyString,
      /**
       * `entered`: the baker entered an amount at the station (a scanned lot with a
       * quantity); `recipe`: the recipe's need was assumed, so `consumed` equals it by
       * construction and is no measurement (UI5A-4).
       */
      qty_basis: z.enum(['entered', 'recipe']),
      /** Its cost per base unit at completion, exact cents (`null`: none). */
      unit_cost_exact: qtyString.nullable(),
    }),
  ),
  /** Every row the completion took from (traceability: which lots went into the batch). */
  consumption: z.array(
    z.object({
      component: stockProductRefSchema,
      lot: lotRefSchema.nullable(),
      location: stockLocationRefSchema,
      state: z.string(),
      qty: qtyString,
      source: z.enum(['fefo', 'scanned', 'beyond_stock']),
      movement_id: z.uuid(),
    }),
  ),
  cost: z.object({
    /** The product's standard cost per base unit at completion. */
    standard_unit_cents: z.int().nullable(),
    /** Standard cost of the actual yield. */
    standard_cents: z.int().nullable(),
    /** Σ consumed × component cost (lots carry no purchase cost until M6: standard basis). */
    actual_cents: z.int().nullable(),
    actual_unit_cents: z.int().nullable(),
    /** Every consumed component had a cost. */
    complete: z.boolean(),
    missing: z.array(stockProductRefSchema),
    basis: z.literal('component_standard_cost'),
  }),
  flags: z.array(flagSchema),
});
/** A batch with everything that happened to it. */
export type BatchDetail = z.infer<typeof batchDetailSchema>;

/** `POST /production/batches` body: a batch planned in the Admin (numbered `PB-SRV-…`). */
export const batchCreateRequestSchema = z.object({
  product_id: z.uuid(),
  /** Default: the account's default branch. */
  branch_id: z.uuid().optional(),
  planned_start: instant,
  planned_qty: positiveQtyString,
  station_id: z.uuid().optional(),
  /** Default: the product's active recipe version (pinned at planning). */
  recipe_version_id: z.uuid().optional(),
  allocations: z.array(batchAllocationInputSchema).max(50).optional(),
  note: z.string().trim().max(500).optional(),
  /**
   * The order line this batch is planned to make (D-074). A custom-item line is covered only
   * by a batch planned for it; the planner chooses what the batch makes. A catalogue line
   * needs a batch of its product (`product_mismatch`).
   */
  for_order_line_id: z.uuid().optional(),
});
/** `POST /production/batches` body. */
export type BatchCreateRequest = z.infer<typeof batchCreateRequestSchema>;

/** `POST /production/batches/:id/cancel` body. */
export const batchCancelRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

/**
 * `PATCH /production/batches/:id` body (M7 planning board): move a **planned** batch to
 * another day or time, change how much it makes, or its note. The recipe version stays
 * pinned. The amount can't go below what is allocated to customer orders (unallocate
 * first); the shelf's share shrinks to fit.
 */
export const batchUpdateRequestSchema = z
  .object({
    planned_start: instant.optional(),
    planned_qty: positiveQtyString.optional(),
    /** `null` clears it. */
    note: z.string().trim().max(500).nullable().optional(),
  })
  .refine(
    (b) => b.planned_start !== undefined || b.planned_qty !== undefined || b.note !== undefined,
    { message: 'Change at least one thing' },
  );
/** `PATCH /production/batches/:id` body. */
export type BatchUpdateRequest = z.infer<typeof batchUpdateRequestSchema>;

// ---------------------------------------------------------------------------------------
// Reports

/** `GET /production/reports/yield-variance` query (completed local days, inclusive). */
export const yieldVarianceReportQuerySchema = z.object({
  from: plainDate,
  to: plainDate,
  product_id: z.uuid().optional(),
  branch_id: z.uuid().optional(),
});
/** `GET /production/reports/yield-variance` response. */
export const yieldVarianceReportSchema = z.object({
  from: plainDate,
  to: plainDate,
  rows: z.array(
    z.object({
      product: stockProductRefSchema,
      batches: count,
      planned_qty: qtyString,
      input_qty: qtyString,
      actual_qty: qtyString,
      vs_planned: varianceSchema,
      vs_recipe: varianceSchema,
    }),
  ),
});

/** `GET /production/reports/usage` query: completed batches in the period. */
export const usageReportQuerySchema = z.object({
  from: plainDate,
  to: plainDate,
  /** Only batches of this product. */
  product_id: z.uuid().optional(),
  /** Only this component. */
  component_id: z.uuid().optional(),
  branch_id: z.uuid().optional(),
});
/** `GET /production/reports/usage` response. */
export const usageReportSchema = z.object({
  from: plainDate,
  to: plainDate,
  batches: count,
  /**
   * What `consumed` is (CR5-16). `recipe_based`: what completions recorded — the recipes'
   * need, except where a baker entered amounts for scanned lots — so the difference shows
   * only those entries, never measured waste. Stock counts don't feed actual usage in V1.
   */
  consumed_basis: z.literal('recipe_based'),
  rows: z.array(
    z.object({
      component: stockProductRefSchema,
      batches: count,
      theoretical: qtyString,
      /** Recorded consumption (see `consumed_basis`). */
      consumed: qtyString,
      difference: qtyString,
      difference_pct: z.string().nullable(),
      beyond_stock: qtyString,
      /** Batches where a baker entered this component's amount at the station (UI5A-4). */
      entered_batches: count,
      /**
       * Over those batches only — the one place a difference means something: the recipes'
       * need, what was entered, and the difference. `null` when no batch entered one.
       */
      entered: z
        .object({
          theoretical: qtyString,
          consumed: qtyString,
          difference: qtyString,
          difference_pct: z.string().nullable(),
        })
        .nullable(),
    }),
  ),
});
